import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../services/api';
import { socket } from '../services/socket';
import ConversationList from '../components/ConversationList';
import ConversationDetail from '../components/ConversationDetail';
import LanguageSwitcher from '../components/LanguageSwitcher';
import OnboardingWizard from '../components/OnboardingWizard';
import {
  MessageCircle,
  BarChart3,
  LogOut,
  Menu,
  X,
  Circle,
  Bot,
  BookOpen,
  Palette,
  Music2,
  Settings as SettingsIcon,
  Loader2,
  HelpCircle,
  Users,
  AlertTriangle,
} from 'lucide-react';
import HelpCenter from '../help/HelpCenter';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, sidebarOpen, setSidebarOpen, conversations, setSelectedConversation, mobileConversationView } = useStore();

  // Check for tab parameter in URL on initial load
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['conversations', 'analytics', 'test-ai', 'knowledge', 'widget-theming', 'orchestrations', 'contacts', 'settings'].includes(tabParam)) {
      return tabParam as 'conversations' | 'analytics' | 'test-ai' | 'knowledge' | 'widget-theming' | 'orchestrations' | 'contacts' | 'settings';
    }
    return 'conversations';
  };

  const [activeTab, setActiveTab] = useState<'conversations' | 'analytics' | 'test-ai' | 'knowledge' | 'widget-theming' | 'orchestrations' | 'contacts' | 'settings'>(getInitialTab());
  const [userStatus, setUserStatus] = useState<'online' | 'away' | 'offline'>('online');
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [showHelpCenter, setShowHelpCenter] = useState(false);

  // Update page title based on active tab
  useEffect(() => {
    const titles: Record<typeof activeTab, string> = {
      'conversations': `${t('nav.conversations')} - tahc`,
      'analytics': `${t('nav.analytics')} - tahc`,
      'test-ai': `${t('nav.testAI')} - tahc`,
      'knowledge': `${t('nav.knowledge')} - tahc`,
      'widget-theming': `${t('nav.widgetTheming')} - tahc`,
      'orchestrations': `${t('nav.orchestrations')} - tahc`,
      'contacts': `${t('nav.contacts')} - tahc`,
      'settings': `${t('nav.settings')} - tahc`,
    };
    document.title = titles[activeTab];
  }, [activeTab, t]);

  // Close sidebar on mobile on initial load
  useEffect(() => {
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, []);

  // Check if user needs onboarding (no knowledge articles)
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Skip onboarding check if user explicitly skipped before
      const skippedOnboarding = localStorage.getItem('skipped_onboarding');
      if (skippedOnboarding === 'true') {
        setShowOnboarding(false);
        setCheckingOnboarding(false);
        return;
      }

      try {
        const stats = await api.getOnboardingStats();
        // Show onboarding if no articles indexed
        setShowOnboarding(stats.total_articles === 0);
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        setShowOnboarding(false);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    if (user) {
      checkOnboardingStatus();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      const storedUser = api.getCurrentUser();
      if (storedUser) {
        setUser(storedUser);
      } else {
        navigate('/login');
        return;
      }
    }

    // Connect socket
    const tokens = localStorage.getItem('auth_tokens');
    if (tokens && tokens !== 'undefined') {
      try {
        const { accessToken } = JSON.parse(tokens);
        socket.connect(accessToken);
      } catch (error) {
        console.error('Failed to parse auth tokens:', error);
        localStorage.removeItem('auth_tokens');
      }
    }

    // Eagerly load conversations on dashboard mount
    api.getConversations().then(convs => {
      useStore.getState().setConversations(convs);
    }).catch(err => {
      console.error('Failed to load conversations:', err);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Handle conversation ID from URL
  const [conversationError, setConversationError] = useState<string | null>(null);

  useEffect(() => {
    const match = location.pathname.match(/\/dashboard\/conversations\/([a-f0-9-]+)/);
    if (match && match[1]) {
      const conversationId = match[1];
      setConversationError(null);
      // Find conversation in existing list
      const conversation = conversations.find(c => c.id === conversationId);
      if (conversation) {
        setSelectedConversation(conversation);
        setActiveTab('conversations');
      } else {
        // Fetch conversation if not in list
        api.getConversations().then(convs => {
          const conv = convs.find((c: any) => c.id === conversationId);
          if (conv) {
            setSelectedConversation(conv);
            setActiveTab('conversations');
          } else {
            // Conversation not found or no access
            setConversationError(t('conversations.notFoundOrNoAccess'));
            setActiveTab('conversations');
          }
        }).catch(() => {
          setConversationError(t('conversations.notFoundOrNoAccess'));
          setActiveTab('conversations');
        });
      }
    }
  }, [location.pathname, conversations, setSelectedConversation, t]);

  const handleStatusChange = async (status: 'online' | 'away' | 'offline') => {
    try {
      await api.updateUserStatus(status);
      socket.updateAgentStatus(status);
      setUserStatus(status);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleLogout = () => {
    api.logout();
    socket.disconnect();
    setUser(null);
    navigate('/login');
  };

  const handleTabChange = (tab: 'conversations' | 'analytics' | 'test-ai' | 'knowledge' | 'widget-theming' | 'orchestrations' | 'contacts' | 'settings') => {
    setActiveTab(tab);
    // Close sidebar on mobile after selecting a tab
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-500';
      case 'away':
        return 'text-yellow-500';
      case 'offline':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  // Handlers for onboarding
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('skipped_onboarding', 'true');
    setShowOnboarding(false);
  };

  // Show loading while checking onboarding status
  if (checkingOnboarding) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show onboarding wizard for new users
  if (showOnboarding) {
    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    );
  }

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0 lg:w-20'
        } bg-black text-white transition-all duration-300 flex flex-col fixed lg:relative z-50 lg:z-auto overflow-hidden lg:h-auto my-2 lg:my-4 ml-2 lg:ml-4 mr-2 lg:mr-0 rounded-3xl border-2 border-white max-h-[calc(100vh-16px)] lg:max-h-[calc(100vh-32px)]`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <>
                <h1 className="text-xl font-bold">tahc.ai</h1>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-gray-800 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </>
            ) : (
              <button
                onClick={() => setSidebarOpen(true)}
                className="mx-auto p-2 hover:bg-gray-800 rounded-full transition"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-3">
          <button
            onClick={() => handleTabChange('conversations')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'conversations'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <MessageCircle className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.conversations')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('test-ai')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'test-ai'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <Bot className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.testAI')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('knowledge')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'knowledge'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <BookOpen className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.knowledge')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('widget-theming')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'widget-theming'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <Palette className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.widgetTheming')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('orchestrations')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'orchestrations'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <Music2 className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.orchestrations')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('contacts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'contacts'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <Users className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.contacts')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'settings'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <SettingsIcon className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.settings')}</span>}
          </button>

          <button
            onClick={() => handleTabChange('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition ${
              activeTab === 'analytics'
                ? 'bg-white text-black'
                : 'text-gray-300 hover:bg-gray-800'
            } ${!sidebarOpen && 'justify-center'}`}
          >
            <BarChart3 className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.analytics')}</span>}
          </button>

          <button
            onClick={() => setShowHelpCenter(true)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-full transition text-gray-300 hover:bg-gray-800 ${!sidebarOpen && 'justify-center'}`}
          >
            <HelpCircle className="w-5 h-5" />
            {sidebarOpen && <span className="font-medium">{t('nav.help')}</span>}
          </button>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-800">
          {sidebarOpen ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold">
                    {user?.name.split(' ').map((n) => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>

              {/* Status Selector */}
              <select
                value={userStatus}
                onChange={(e) => handleStatusChange(e.target.value as any)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-gray-600"
              >
                <option value="online" className="text-gray-900">🟢 {t('common.online')}</option>
                <option value="away" className="text-gray-900">🟡 {t('common.away')}</option>
                <option value="offline" className="text-gray-900">⚫ {t('common.offline')}</option>
              </select>

              {/* Language Switcher - Full variant for expanded sidebar */}
              <div className="w-full">
                <LanguageSwitcher variant="full" />
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-full transition text-sm font-medium"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('auth.logout')}</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold">
                  {user?.name.split(' ').map((n) => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <Circle className={`w-3 h-3 ${getStatusColor(userStatus)} fill-current`} />

              {/* Language Switcher - Compact variant for collapsed sidebar */}
              <div className="flex justify-center">
                <LanguageSwitcher variant="compact" />
              </div>

              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-800 rounded-full transition"
                title={t('auth.logout')}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
        {/* Mobile Header with Hamburger */}
        {!sidebarOpen && (
          <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-gray-200 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <Menu className="w-6 h-6 text-gray-900" />
            </button>
            <h1 className="text-lg font-bold text-gray-900">tahc.ai</h1>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        )}

        {activeTab === 'conversations' ? (
          <div className="flex-1 flex flex-col lg:flex-row relative">
            {conversationError && (
              <div className="absolute top-0 left-0 right-0 z-10 bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{conversationError}</span>
                </div>
                <button
                  onClick={() => setConversationError(null)}
                  className="text-yellow-800 hover:text-yellow-900 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className={`w-full lg:w-96 border-r border-gray-200 flex-shrink-0 ${mobileConversationView ? 'hidden lg:flex' : 'flex'} flex-col h-full ${conversationError ? 'pt-12' : ''}`}>
              <ConversationList />
            </div>
            <div className={`flex-1 ${mobileConversationView ? 'flex' : 'hidden lg:flex'} flex-col h-full overflow-hidden ${conversationError ? 'pt-12' : ''}`}>
              <ConversationDetail />
            </div>
          </div>
        ) : activeTab === 'test-ai' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <TestAI />
          </div>
        ) : activeTab === 'knowledge' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <KnowledgeBase />
          </div>
        ) : activeTab === 'widget-theming' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <WidgetTheming />
          </div>
        ) : activeTab === 'orchestrations' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <Orchestrations />
          </div>
        ) : activeTab === 'contacts' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <Contacts />
          </div>
        ) : activeTab === 'settings' ? (
          <div className="flex-1 flex flex-col min-h-0">
            <Settings />
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <Analytics />
          </div>
        )}
      </div>

      {/* Help Center Modal */}
      <HelpCenter
        isOpen={showHelpCenter}
        onClose={() => setShowHelpCenter(false)}
      />
    </div>
  );
}

// Import components inline to avoid circular dependencies
import Analytics from './Analytics';
import TestAI from './TestAI';
import KnowledgeBase from './KnowledgeBase';
import WidgetTheming from './WidgetTheming';
import Orchestrations from './Orchestrations';
import Contacts from './Contacts';
import Settings from './Settings';
